import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { ServerUrl } from "../config";

import UtteranceTrackerModal from "./UtteranceTrackerModal";

function AdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleSettings, setRoleSettings] = useState([]);
  const [adminStats, setAdminStats] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState("users");
  const [isTrackerOpen, setIsTrackerOpen] = useState(false);
  const [trackerUserEmail, setTrackerUserEmail] = useState("");

  const permissionsString = localStorage.getItem("permissions");
  let permissions = null;
  try {
    permissions = permissionsString ? JSON.parse(permissionsString) : null;
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }

  const userRole = localStorage.getItem("role") || "user";
  const isSuper = userRole === "superadmin";

  // Redirect users who cannot manage users
  useEffect(() => {
    if (!permissions || !permissions.canManageUsers) {
      Swal.fire("Access Denied", "You do not have permission to access user management.", "error");
      navigate("/audio");
    }
  }, [permissions, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setUsers(response.data);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/role-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setRoleSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch role settings:", error);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/admin-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setAdminStats(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch admin stats:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    if (isSuper) {
      fetchRoleSettings();
      fetchAdminStats();
    }
  }, [isSuper]);

  const updateRole = async (email, currentRole) => {
    const roleOptions = {
      user: "User",
      admin: "Admin",
      superadmin: "Super Admin"
    };

    const { value: newRole } = await Swal.fire({
      title: "Update User Role",
      input: "select",
      inputOptions: roleOptions,
      inputValue: currentRole,
      showCancelButton: true,
      inputPlaceholder: "Select a role",
      inputValidator: (value) => {
        if (!value) {
          return "You need to select a role";
        }
      }
    });

    if (newRole && newRole !== currentRole) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${ServerUrl}api/updateuser`, {
          email,
          role: newRole,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          Swal.fire("Success", "User role updated successfully", "success");
          setUsers(users.map((user) =>
            user.email === email ? { ...user, role: newRole } : user
          ));
        } else {
          throw new Error("Failed to update role");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to update role", "error");
      }
    }
  };

  const resetPassword = async (email) => {
    const { value: newPassword } = await Swal.fire({
      title: `Reset password for ${email}`,
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password",
      inputAttributes: {
        autocapitalize: "off",
        autocorrect: "off"
      },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value) {
          return "You need to write a password!";
        }
      }
    });

    if (newPassword) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${ServerUrl}api/reset-password`,
          { email, password: newPassword },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status === 200) {
          Swal.fire("Success", "Password reset successfully!", "success");
        } else {
          throw new Error("Failed to reset password");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to reset password", "error");
      }
    }
  };

  const handleDeleteUser = async (email) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete user: ${email}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete"
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.delete(`${ServerUrl}api/deleteuser`, {
          data: { email },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          Swal.fire("Deleted!", "User deleted successfully", "success");
          fetchUsers();
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to delete user", "error");
      }
    }
  };

  const handleBack = () => {
    navigate("/audio");
  };

  const handleRegisterUser = async () => {
    const roleSelectHtml = isSuper 
      ? '<select id="swal-input3" class="swal2-select" style="border: 1px solid black; width: 80%; padding: 8px; margin: 10px 0; font-size: 16px;">' +
        '<option value="user">User</option>' +
        '<option value="admin">Admin</option>' +
        '<option value="superadmin">Super Admin</option>' +
        "</select>"
      : '<select id="swal-input3" class="swal2-select" style="border: 1px solid black; width: 80%; padding: 8px; margin: 10px 0; font-size: 16px;">' +
        '<option value="user">User</option>' +
        "</select>";

    const { value: formValues } = await Swal.fire({
      title: "Register New User",
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Email" type="email">' +
        '<input id="swal-input2" class="swal2-input" placeholder="Password" type="password">' +
        roleSelectHtml,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Register",
      preConfirm: () => {
        const email = document.getElementById("swal-input1").value;
        const password = document.getElementById("swal-input2").value;
        const role = document.getElementById("swal-input3").value;

        if (!email || !password || !role) {
          Swal.showValidationMessage("Please fill in all fields");
          return false;
        }
        return { email, password, role };
      },
    });

    if (formValues) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${ServerUrl}api/register`, {
          email: formValues.email,
          password: formValues.password,
          role: formValues.role,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 201 || response.status === 200) {
          Swal.fire("Success", response.data.message || "User registered successfully", "success");
          fetchUsers();
        } else {
          throw new Error("Failed to register user");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to register user", "error");
      }
    }
  };

  const handleToggleTab = (role, tab) => {
    setRoleSettings(roleSettings.map(s => {
      if (s.role === role) {
        const visibleTabs = s.visibleTabs.includes(tab)
          ? s.visibleTabs.filter(t => t !== tab)
          : [...s.visibleTabs, tab];
        return { ...s, visibleTabs };
      }
      return s;
    }));
  };

  const handleTogglePermission = (role, permission) => {
    setRoleSettings(roleSettings.map(s => {
      if (s.role === role) {
        return {
          ...s,
          permissions: {
            ...s.permissions,
            [permission]: !s.permissions[permission]
          }
        };
      }
      return s;
    }));
  };

  const handleSaveSettings = async (role) => {
    const setting = roleSettings.find(s => s.role === role);
    if (!setting) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${ServerUrl}api/role-settings/${role}`, {
        visibleTabs: setting.visibleTabs,
        permissions: setting.permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        Swal.fire("Success", `Role layout settings updated for ${role}`, "success");
        // If updating self, update active localStorage configuration
        if (role === userRole) {
          localStorage.setItem("visibleTabs", JSON.stringify(setting.visibleTabs));
          localStorage.setItem("permissions", JSON.stringify(setting.permissions));
        }
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to save settings", "error");
    }
  };

  return (
    <div className="w-screen h-screen bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white p-6 sm:p-8 md:p-10 rounded-xl shadow-xl space-y-6 w-full max-w-6xl">
        <h1 className="text-2xl sm:text-3xl font-semibold text-center text-gray-800 mb-6">Admin Panel</h1>
        
        <div className="flex justify-between items-center flex-wrap gap-4">
          <button onClick={handleBack} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm">
            Back to Home
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setTrackerUserEmail("");
                setIsTrackerOpen(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm font-semibold flex items-center gap-1 shadow-sm"
            >
              📊 Utterances Progress & Pending Tracker
            </button>
            {activeAdminTab === "users" && (
              <button onClick={handleRegisterUser} className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm font-semibold">
                Register User
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation for Superadmin */}
        {isSuper && (
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveAdminTab("users")}
              className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
                activeAdminTab === "users"
                  ? "border-b-2 border-blue-500 text-blue-500 font-bold"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              👥 User Management
            </button>
            <button
              onClick={() => setActiveAdminTab("stats")}
              className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
                activeAdminTab === "stats"
                  ? "border-b-2 border-blue-500 text-blue-500 font-bold"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📊 Admin Stats
            </button>
          </div>
        )}

        {activeAdminTab === "users" && (
          loading ? (
            <div className="loader mx-auto"></div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="bg-gray-200 text-gray-700 uppercase font-semibold">
                    <th className="py-3 px-4 border-b text-left">Email</th>
                    <th className="py-3 px-4 border-b text-left">Role</th>
                    <th className="py-3 px-4 border-b text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id} className="text-gray-700 hover:bg-gray-50 transition-colors border-b">
                      <td className="py-3 px-4 truncate font-medium">{user.email}</td>
                      <td className="py-3 px-4 capitalize font-medium">{user.role}</td>
                      <td className="py-3 px-4 flex justify-center space-x-2 flex-wrap gap-y-1">
                        <button
                          onClick={() => {
                            setTrackerUserEmail(user.email);
                            setIsTrackerOpen(true);
                          }}
                          className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 text-xs font-semibold"
                        >
                          View Progress
                        </button>
                        {isSuper && (
                          <button
                            onClick={() => updateRole(user.email, user.role)}
                            className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-xs font-semibold"
                          >
                            Update Role
                          </button>
                        )}
                        <button
                          onClick={() => resetPassword(user.email)}
                          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-xs font-semibold"
                        >
                          Reset Password
                        </button>
                        {user.role !== "superadmin" && (
                          <button
                            onClick={() => handleDeleteUser(user.email)}
                            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs font-semibold"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
        
        {activeAdminTab === "settings" && (
          /* Settings configuration tab content */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roleSettings.map((setting) => (
              <div key={setting.role} className="border border-gray-200 rounded-xl p-5 shadow-md bg-gray-50 space-y-4">
                <h3 className="text-lg font-bold text-gray-800 capitalize border-b pb-2">
                  Role: {setting.role === "superadmin" ? "Super Admin" : setting.role === "admin" ? "Admin" : "User"}
                </h3>
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Visible Tabs</h4>
                  <div className="space-y-1">
                    {["audio", "channel", "app"].map((tab) => (
                      <label key={tab} className="flex items-center text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={setting.visibleTabs.includes(tab)}
                          onChange={() => handleToggleTab(setting.role, tab)}
                          className="mr-2 rounded text-blue-500 focus:ring-blue-400"
                        />
                        {tab === "audio" ? "🔊 Audio Upload" : tab === "channel" ? "📺 Channel Upload" : "📱 App Upload"}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Permissions</h4>
                  <div className="space-y-1">
                    {[
                      { key: "canManageUsers", label: "👥 Manage Users (Admin Panel)" },
                      { key: "canUploadAudio", label: "🔊 Upload / Record Audio" },
                      { key: "canUploadChannels", label: "📺 Upload / Manage Channels" },
                      { key: "canUploadApps", label: "📱 Upload / Manage Apps" }
                    ].map((perm) => (
                      <label key={perm.key} className="flex items-center text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={setting.permissions[perm.key]}
                          onChange={() => handleTogglePermission(setting.role, perm.key)}
                          className="mr-2 rounded text-blue-500 focus:ring-blue-400"
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleSaveSettings(setting.role)}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition-colors text-sm"
                >
                  Save Settings
                </button>
              </div>
            ))}
          </div>
        )}

        {activeAdminTab === "stats" && (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="bg-gray-200 text-gray-700 uppercase font-semibold">
                  <th className="py-3 px-4 border-b text-left">Admin Email</th>
                  <th className="py-3 px-4 border-b text-center">Users Registered</th>
                  <th className="py-3 px-4 border-b text-center">Total Audios</th>
                </tr>
              </thead>
              <tbody>
                {adminStats.map((stat, idx) => (
                  <tr key={idx} className="text-gray-700 hover:bg-gray-50 transition-colors border-b">
                    <td className="py-3 px-4 truncate">{stat.adminEmail}</td>
                    <td className="py-3 px-4 text-center">{stat.usersRegistered}</td>
                    <td className="py-3 px-4 text-center">{stat.totalAudios}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <UtteranceTrackerModal
          isOpen={isTrackerOpen}
          onClose={() => setIsTrackerOpen(false)}
          targetUserEmail={trackerUserEmail}
        />
      </div>
    </div>
  );
}

export default AdminPage;